import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { PAGE_URLS } from "@constants/common";
import { ErrorResponseInterface } from "interfaces/common.interface";
import { useEffect } from "react";
import { useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styles from "./VerifyOtp.module.scss";
import { CircularProgress, FormControl } from "@mui/material";
import OTPInput from "@features/common/otpInputField";
import WarningBlock from "@features/common/warningBlock/WarningBlock";
import Button from "@features/common/button/Button";
import IntervalTimer from "../intervalTimer/IntervalTimer";
import { LoginInfoIcon, YggLogo } from "@features/common/SvgIcons";
import HelpText from "../helpText/HelpText";
import FbWarning from "../fbWarning/FbWarning";
import { getLastToken } from "@utils/common";
import {
  useResendOtpForgotPasswordMutation,
  useResendOtpLoginMutation,
  useVerifyOtpForgotPasswordMutation,
  useVerifyOtpLoginMutation,
} from "../authApi";
import { notifyUser } from "@redux/slices/CommonSlice";
import {
  selectoAuthToken,
  setAccessToken,
  setCorporateSelection,
} from "@redux/slices/AuthSlice";
import { handleRedirectByTab } from "@utils/redirectUtils";

/**
 * @interface VerifyOtpProps
 * @description Props for the VerifyOtp component
 *
 * @property {boolean} [isForgotPassword] - Flag to determine if OTP verification is for forgot password flow
 * @property {any} [otpUrls] - Object containing OTP-related API URLs (verify_otp_url, resend_otp_url)
 * @property {string} [verifiedEmail] - Email address that was verified in the previous step
 */
interface VerifyOtpProps {
  isForgotPassword?: boolean;
  otpUrls?: any;
  verifiedEmail?: string;
}

/**
 * @component VerifyOtp
 * @description OTP verification component used in both login and forgot password flows.
 * Handles 6-digit OTP input, verification, resend functionality, and error handling.
 *
 * @features
 * - 6-digit OTP input with auto-focus
 * - Supports both login and forgot password OTP verification
 * - OTP resend functionality with 60-second cooldown timer
 * - Real-time OTP validation (numbers only)
 * - Error handling for invalid OTP and rate limiting (429 errors)
 * - Warning block display for throttling messages
 * - Auto-enables verify button when 6 digits are entered
 * - Loading states for verify and resend operations
 * - Help text and trouble signing in support
 * - Automatic navigation on successful verification
 *
 * @param {VerifyOtpProps} props - Component props
 * @returns {JSX.Element} OTP verification form with input field, verify button, and resend functionality
 */
/** OTP verification component for both login and forgot password flows */
const VerifyOtp = ({
  isForgotPassword,
  otpUrls,
  verifiedEmail,
}: VerifyOtpProps) => {
  const [otpValue, setOtpValue] = useState("");
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true);
  const [throttledUpto, setThrottledUpto] = useState(60);
  const [otpError, setOtpError] = useState("");
  const [showWarning, setShowWarning] = useState("");
  const [showFbWarning, setShowFbWarning] = useState<boolean>(false);
  const verifyButtonRef: any = useRef(null);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const authToken = useAppSelector(selectoAuthToken);
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get("redirect");

  const handleFBLogin = () => {
    setShowFbWarning(true);
  };

  const inputOnChange = (otpCode: any) => {
    setOtpValue(otpCode);
    setOtpError("");
    const re = /^[0-9\b]+$/;

    if (otpCode === "") {
      setShowWarning("");
    }

    if (otpCode === "" || re.test(otpCode)) {
      setOtpValue(otpCode);
      if (otpCode.length == 6) {
        setButtonDisabled(false);
      } else {
        setButtonDisabled(true);
      }
    } else {
      setOtpValue("");
    }
  };

  const [
    verifyOtpLogin,
    {
      error: otpLoginError,
      isSuccess: otpLoginSuccess,
      data: otpLoginData,
      isLoading: otpLoginLoading,
    },
  ] = useVerifyOtpLoginMutation();

  const [
    verifyOtpForgotPassword,
    {
      error: otpForgotError,
      isSuccess: otpForgotSuccess,
      data: otpForgotData,
      isLoading: otpForgotLoading,
    },
  ] = useVerifyOtpForgotPasswordMutation();

  const [
    resendOtpLogin,
    {
      data: resendOtpLoginData,
      isSuccess: resendOtpLoginSuccess,
      error: resendOtpLoginError,
      isLoading: resendOtpLoginLoading,
    },
  ] = useResendOtpLoginMutation();

  const [
    resendOtpForgotPassword,
    {
      data: resendOtpForgotPasswordData,
      isSuccess: resendOtpForgotPasswordSuccess,
      error: resendOtpForgotPasswordError,
      isLoading: resendOtpForgotPasswordLoading,
    },
  ] = useResendOtpForgotPasswordMutation();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isForgotPassword) {
      verifyOtpForgotPassword({
        otp: otpValue,
        token: getLastToken(otpUrls.verify_otp_url),
        username: verifiedEmail || "",
      });
    } else {
      verifyOtpLogin({
        otp: otpValue,
        token: getLastToken(otpUrls.verify_otp_url),
      });
    }
  };

  const clearOtpField = () => {
    setOtpValue("");
    setOtpError("");
  };

  const handleResendOtpClick = () => {
    if (isForgotPassword) {
      handleresendOtpForgotPasswordClick();
    } else {
      handleResendOtpLoginClick();
    }
  };

  const handleResendOtpLoginClick = () => {
    clearOtpField();
    setThrottledUpto(60);
    resendOtpLogin({
      username: verifiedEmail || "",
      token: getLastToken(otpUrls.resend_otp_url),
      action: "login",
    });
  };

  const handleresendOtpForgotPasswordClick = () => {
    clearOtpField();
    setThrottledUpto(60);
    resendOtpForgotPassword({
      username: verifiedEmail || "",
      token: getLastToken(otpUrls.resend_otp_url),
      action: "password_reset",
    });
  };

  // Handle OTP verification response
  useEffect(() => {
    if (isForgotPassword) {
      if (otpForgotSuccess) {
        navigate(
          `${PAGE_URLS.RESET_PASSWORD}?email=${encodeURIComponent(
            verifiedEmail || "",
          )}`,
        );
      }
      if (otpForgotError) {
        if ((otpForgotError as any)?.status === 400) {
          setOtpError((otpForgotError as ErrorResponseInterface).data?.message);
        } else if ((otpForgotError as any)?.status === 429) {
          setOtpError((otpForgotError as any)?.data?.errors?.message);
        }
      }
    } else {
      if (otpLoginSuccess) {
        // Store access token in Redux store if present
        if (otpLoginData?.data?.access_token) {
          dispatch(setAccessToken(otpLoginData?.data?.access_token));
        }
        dispatch(
          setCorporateSelection(!!otpLoginData?.data?.show_corporate_selection),
        );

        const decodedRedirectUrl = redirectUrl
          ? decodeURIComponent(redirectUrl)
          : null;

        /**
         * Priority 1: Corporate selection
         * Pass redirectUrl if available
         */

        if (otpLoginData?.data?.show_corporate_selection) {
          const corporateUrl = decodedRedirectUrl
            ? `${PAGE_URLS.SELECT_CORPORATE}?redirect=${encodeURIComponent(
                decodedRedirectUrl,
              )}`
            : PAGE_URLS.SELECT_CORPORATE;
          navigate(corporateUrl);
          return;
        } else {
          localStorage.setItem("show_corporate_selection", "true"); // Disable corporate selection for users with only one corporate
        }

        /**
         * Priority 2: Redirect URL
         */
        if (decodedRedirectUrl) {
          window.location.href = decodedRedirectUrl;
          return;
        }

        /**
         * Priority 3: Default fallback
         */

        handleRedirectByTab(
          otpLoginData?.data?.redirect_tab || PAGE_URLS.ISSUE_CREDITS,
          navigate,
        );
      }
      if (otpLoginError) {
        if ((otpLoginError as any)?.status === 400) {
          setOtpError((otpLoginError as ErrorResponseInterface).data?.message);
        }
        if ((otpLoginError as any)?.status === 429) {
          setOtpError((otpLoginError as any)?.data?.errors?.message);
        }
      }
    }
  }, [
    isForgotPassword,
    otpForgotSuccess,
    otpForgotError,
    otpLoginSuccess,
    otpLoginError,
    otpLoginData,
    dispatch,
    navigate,
  ]);

  // Handle resend login OTP response
  useEffect(() => {
    if (resendOtpLoginSuccess) {
      dispatch(notifyUser({ message: resendOtpLoginData.message }));
    }

    if (resendOtpLoginError) {
      if ((resendOtpLoginError as any)?.status === 400) {
        setOtpError((resendOtpLoginError as any).data?.message);
      } else if ((resendOtpLoginError as any)?.status === 429) {
        setShowWarning((resendOtpLoginError as any).data?.errors?.message);
      }
    }
  }, [resendOtpLoginSuccess, resendOtpLoginData, resendOtpLoginError]);

  // Handle resend forgot password OTP response
  useEffect(() => {
    if (resendOtpForgotPasswordSuccess) {
      dispatch(notifyUser({ message: resendOtpForgotPasswordData.message }));
    }

    if (resendOtpForgotPasswordError) {
      if ((resendOtpForgotPasswordError as any)?.status === 400) {
        setOtpError((resendOtpForgotPasswordError as any).data?.message);
      } else if ((resendOtpForgotPasswordError as any)?.status === 429) {
        setShowWarning(
          (resendOtpForgotPasswordError as any).data?.errors?.message,
        );
      }
    }
  }, [
    resendOtpForgotPasswordSuccess,
    resendOtpForgotPasswordData,
    resendOtpForgotPasswordError,
    dispatch,
  ]);

  return (
    <>
      <div className={styles["verify-otp"]}>
        <div className={styles["header"]}>
          <YggLogo />
          <h5>Enter Verification Code</h5>
        </div>
        <h3>Enter verification code sent to your email</h3>

        <div className={styles["verify-otp__list"]}>
          <form onSubmit={handleSubmit}>
            <ul className={styles["verify-otp__list"]}>
              <li className={styles["verify-otp__list-item"]}>
                <FormControl variant="standard" fullWidth={true}>
                  <OTPInput
                    value={otpValue}
                    onChange={inputOnChange}
                    numInputs={6}
                    renderInput={(props) => <input {...props} />}
                    containerStyle={`${
                      styles["verify-otp__list-item__input-field"]
                    } ${
                      !showWarning?.length
                        ? styles["input-single"]
                        : styles["input-double"]
                    }`}
                    inputStyle={
                      otpError ? "otp-input-field-error" : "otp-input-field"
                    }
                    shouldAutoFocus={true}
                    inputType={"number"}
                  />
                </FormControl>
              </li>

              {otpError && (
                <p className={` ${styles["verify-otp__invalid-otp"]}`}>
                  {otpError}
                </p>
              )}

              {showWarning && <WarningBlock message={showWarning} />}

              {!showWarning?.length && (
                <li
                  className={`button-container ${styles["verify-otp__list-item"]} ${styles["verify-otp__verify-btn"]}`}
                >
                  <Button
                    buttonRef={verifyButtonRef}
                    theme="purple"
                    className={`submit-button 
                            ${styles["verify-otp__verify-btn__button"]}`}
                    attribues={{
                      disabled:
                        buttonDisabled ||
                        (isForgotPassword ? otpForgotLoading : otpLoginLoading),
                      type: "submit",
                    }}
                  >
                    Verify
                  </Button>
                </li>
              )}
            </ul>
          </form>

          {!showWarning?.length && (
            <div className={styles["otp-resend"]}>
              {throttledUpto !== 0 ? (
                <IntervalTimer
                  onComplete={() => {
                    setThrottledUpto(0);
                  }}
                  startingValue={throttledUpto}
                />
              ) : (
                <p
                  className={`${styles["resend-btn"]}`}
                  onClick={handleResendOtpClick}
                >
                  {resendOtpLoginLoading ? <CircularProgress /> : "Resend Otp"}
                </p>
              )}
            </div>
          )}

          <div className={styles["trouble-signin-container"]}>
            <div className={styles["trouble-signin"]}>
              <LoginInfoIcon />
              <span className={styles["content"]} onClick={handleFBLogin}>
                Trouble signing in?
              </span>
            </div>
            <HelpText />
          </div>
        </div>

        <FbWarning open={showFbWarning} onClose={setShowFbWarning} />
      </div>
    </>
  );
};

export default VerifyOtp;
